import { useEffect } from 'react';
import {
  Badge,
  Text,
  Group,
  Stack,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Box,
  Anchor,
} from '@mantine/core';
import { IconX, IconMailOpened } from '@tabler/icons-react';
import { useNavigate } from 'react-router';
import { isAbsoluteFileUrl } from '@ayocore/platform/filemanager/common/fileUrl';
import { resolveFileUrl } from '@ayocore/platform/filemanager/client/fileUrl';
import { useTab } from '@ayocore/platform/client/tabs';
import type { Notification } from '../../schemas/notification.schema';
import { useNotifications } from '../useNotifications';
import { PushSettingsButton } from './PushSettings';
import { MarkAllReadButton } from './MarkAllReadButton';
import { DeleteAllButton } from './DeleteAllButton';
import classes from './Notifications.module.css';

/**
 * Компонент уведомлений.
 * Независимый компонент, который может быть размещён где угодно.
 */
/**
 * Ссылка ведёт на сервер, а не на экран SPA: абсолютный URL (s3-public, внешний
 * сервис) либо серверный эндпоинт `/api/...` (скачивание файла, отчёт).
 * Сам резолв в рабочий URL — `resolveFileUrl` (канон common/fileUrl), здесь
 * только классификация: утилита про файлы, про маршруты SPA она не знает.
 */
function isServerLink(url: string): boolean {
  return isAbsoluteFileUrl(url) || url.startsWith('/api/');
}

/**
 * Время, которое показывает строка ленты.
 *
 * У схлопнутого уведомления это время последнего события: строка стоит наверху,
 * и дата первого события выглядела бы в ней ошибкой.
 */
function getNotificationTimestamp(notification: Notification): Date {
  const source = notification.last_event_at ?? notification.created_at;
  return source instanceof Date ? source : new Date(source);
}

/** Дата и время строки ленты в локальном формате */
function formatNotificationDate(value: Date): string {
  const date = value.toLocaleDateString('ru-RU');
  const time = value.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

export function Notifications({ isModal = false }: { isModal?: boolean } = {}) {
  const { notifications, unreadCount, isLoading, markAsRead, deleteNotification } =
    useNotifications();
  const navigate = useNavigate();
  const { setTabTitle } = useTab();

  useEffect(() => {
    if (!isModal) {
      setTabTitle('Уведомления');
    }
  }, [setTabTitle, isModal]);

  const handleNotificationClick = (notification: (typeof notifications)[0]) => {
    if (!notification.entity_url) return;

    // Не всякий entity_url — маршрут SPA: ссылки на файлы и серверные эндпоинты
    // (`/api/...`, абсолютные) роутер открыть не может — на dev он бы увёл на
    // фронтовый порт, где такого пути нет. Их отдаём браузеру.
    if (isServerLink(notification.entity_url)) {
      window.open(resolveFileUrl(notification.entity_url), '_blank', 'noopener');
    } else {
      navigate(notification.entity_url);
    }
    // Отмечаем как прочитанное при клике
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  /**
   * Получить отображаемый тайтл нотификации с учетом payload
   */
  const getNotificationTitle = (notification: (typeof notifications)[0]): string => {
    const payload = notification.payload as { entity_name?: string } | null | undefined;
    if (payload?.entity_name) {
      return payload.entity_name;
    }
    return notification.title;
  };

  if (isLoading) {
    return (
      <Stack gap='xs' className={classes.container}>
        <Group gap={4} p='xs' className={classes.header}>
          <Text size='xs' fw={600}>
            Уведомления
          </Text>
          <PushSettingsButton />
        </Group>
        <Box p='md'>
          <Text size='sm' c='dimmed'>
            Загрузка уведомлений...
          </Text>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack gap='xs' className={classes.container}>
      {/* Заголовок */}
      <Group justify='space-between' gap='xs' p='xs' className={classes.header}>
        <Group gap={4}>
          <Text size='xs' fw={600}>
            Уведомления
          </Text>
          <PushSettingsButton />
          {unreadCount > 0 && (
            <Badge size='xs' color='red' variant='filled'>
              {unreadCount}
            </Badge>
          )}
        </Group>
        <Group gap={4}>
          <MarkAllReadButton />
          <DeleteAllButton />
        </Group>
      </Group>

      {/* История уведомлений */}
      <ScrollArea className={classes.notificationsScrollArea}>
        <Box p='xs'>
          {notifications.length === 0 ? (
            <Text size='xs' c='dimmed' ta='center' py='xl'>
              Уведомлений пока нет
            </Text>
          ) : (
            <Stack gap='xs'>
              {notifications.map(notification => (
                <Box
                  key={notification.id}
                  p='xs'
                  className={classes.notificationItem}
                  style={{
                    backgroundColor: notification.is_read ? undefined : 'rgba(255, 255, 255, 0.6)',
                    cursor: notification.entity_url ? 'pointer' : 'default',
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <Group justify='space-between' align='flex-start' wrap='nowrap'>
                    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                      <Group gap={4} wrap='nowrap'>
                        {notification.entity_url ? (
                          <Anchor
                            size='xs'
                            fw={500}
                            className={classes.notificationTitle}
                            style={{ cursor: 'pointer' }}
                            onClick={e => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                          >
                            {getNotificationTitle(notification)}
                          </Anchor>
                        ) : (
                          <Text size='xs' fw={500} className={classes.notificationTitle}>
                            {getNotificationTitle(notification)}
                          </Text>
                        )}

                        {/* Сколько событий схлопнуто в эту строку */}
                        {(notification.group_count ?? 1) > 1 && (
                          <Badge size='xs' variant='light' color='gray'>
                            ×{notification.group_count}
                          </Badge>
                        )}
                      </Group>

                      <Text size='xs' c='dimmed' style={{ wordBreak: 'break-word' }}>
                        {notification.message}
                      </Text>

                      <Text size='xs' c='dimmed' style={{ fontSize: '10px' }}>
                        {formatNotificationDate(getNotificationTimestamp(notification))}
                      </Text>
                    </Stack>

                    <Group gap={4} wrap='nowrap'>
                      {!notification.is_read && (
                        <Tooltip label='Отметить прочитанным'>
                          <ActionIcon
                            size='sm'
                            variant='subtle'
                            color='blue'
                            onClick={e => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                          >
                            <IconMailOpened size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}

                      <Tooltip label='Удалить'>
                        <ActionIcon
                          size='sm'
                          variant='subtle'
                          color='red'
                          onClick={e => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </ScrollArea>
    </Stack>
  );
}
